import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { getCachedOrFetchFeatureValue } from "@mirage/service-experimentation";
import { tagged } from "@mirage/service-logging";
import { getFirstLoginTime } from "@mirage/service-onboarding";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import Sentry from "@mirage/shared/sentry";
import { isEsvConfig } from "@mirage/shared/util/esv";
import { WindowNames } from "@mirage/shared/window/types";
import { useCallback, useEffect, useState } from "react";
import { useFeatureFlagValueBool } from "./useFeatureFlagValue";
import { convertFeatureValueToBool } from "./util";

const logger = tagged("dual-mode-hook");

const DUAL_MODE_ENABLED_DATE = 1743490800000; // Tuesday, April 1, 2025
const SERP_TIP_MAX_SEEN = 4;
const LAUNCHER_TOOLTIP_MAX_SEEN = 3;

export async function getIsDualModeEnabled() {
  return convertFeatureValueToBool(
    await getCachedOrFetchFeatureValue("dash_2024_12_11_dual_mode"),
  );
}
export function getIsLauncher() {
  if (typeof window !== "undefined") {
    return !!window?.IS_LAUNCHER;
  } else {
    Sentry.captureException(
      new Error("getIsLauncher called in a non-browser environment"),
    );
    return false;
  }
}

export const useDualMode = () => {
  const isLauncher = getIsLauncher();
  const isDesktop = EnvCtx.surface === "desktop";
  const { onboardingValues } = useOnboardingValues();
  const [usedDashBeforeDualMode, setUsedDashBeforeDualMode] = useState(false);

  const isDualModeEnabledForDash =
    useFeatureFlagValueBool("dash_2024_12_11_dual_mode") && isDesktop;

  const isDualModeEnabledForEsv =
    useFeatureFlagValueBool("desktop_dash_dual_mode") && isDesktop;

  const isDualModeEnabled = isEsvConfig()
    ? isDualModeEnabledForEsv
    : isDualModeEnabledForDash;

  const isLauncherSidePanelEnabled =
    useFeatureFlagValueBool("dash_2025_05_28_desktop_launcher_side_panel") &&
    isDesktop;

  const isDualModeLauncher = isDualModeEnabled && isLauncher;
  const isDualModePersistent = isDualModeEnabled && !isLauncher;

  const getCurrentWindowName = useCallback(() => {
    if (isDualModeEnabled && isDualModeLauncher) {
      return WindowNames.LAUNCHER;
    }
    return WindowNames.PERSISTENT;
  }, [isDualModeEnabled, isDualModeLauncher]);

  useEffect(() => {
    getFirstLoginTime("desktop", "dash_native")
      .then((firstLoginTime) => {
        if (firstLoginTime) {
          setUsedDashBeforeDualMode(firstLoginTime < DUAL_MODE_ENABLED_DATE);
        }
        return;
      })
      .catch((err) => {
        logger.error("failed to getFirstLoginTime", err);
      });
  }, []);

  const showLauncherSearchTip =
    isDualModeEnabled &&
    onboardingValues?.dismissedLauncherSearchTip === false &&
    typeof onboardingValues?.seenLauncherSearchTip === "number" &&
    onboardingValues.seenLauncherSearchTip < SERP_TIP_MAX_SEEN;

  const showLauncherTooltip =
    isDualModeEnabled &&
    onboardingValues?.dismissedLauncherTooltip === false &&
    typeof onboardingValues?.seenLauncherTooltipCount === "number" &&
    onboardingValues.seenLauncherTooltipCount < LAUNCHER_TOOLTIP_MAX_SEEN;

  const showFullScreenTip =
    isDualModeEnabled &&
    onboardingValues?.seenFullScreenDualModeTip === false &&
    usedDashBeforeDualMode;

  return {
    isDualModeEnabled,
    isLauncherSidePanelEnabled,
    isDualModeLauncher,
    isDualModePersistent,
    showLauncherSearchTip,
    showLauncherTooltip,
    showFullScreenTip,
    getCurrentWindowName,
  };
};
